import React, { useState } from "react";
import { X, ArrowLeft, ArrowRight, Coins, Shield, Calendar, FileSignature, CheckCircle2, AlertCircle, Send } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const eur = (n) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n || 0));

const CONTINGENCY_OPTIONS = [
  { v: "bouwkundige_keuring", label: "Bouwkundige keuring", desc: "Bod is afhankelijk van uitkomst van een bouwkundige inspectie" },
  { v: "taxatie", label: "Taxatie", desc: "Bod is afhankelijk van een taxatiewaarde ≥ koopsom" },
  { v: "verkoop_eigen_woning", label: "Verkoop eigen woning", desc: "Bod is afhankelijk van verkoop van uw huidige woning" },
  { v: "nvm_akte", label: "NVM koopakte", desc: "U wenst gebruik van de model NVM koopakte" },
];

const FINANCING = [
  { v: "eigen_middelen", label: "Eigen middelen (contant)", res: false },
  { v: "hypotheek_geregeld", label: "Hypotheek geregeld", res: false },
  { v: "hypotheek_nodig", label: "Hypotheek nodig", res: true },
  { v: "onbekend", label: "Nog niet duidelijk", res: true },
];

const STEPS = [
  { k: "bedrag", label: "Bedrag", icon: Coins },
  { k: "financiering", label: "Financiering", icon: Shield },
  { k: "voorwaarden", label: "Voorwaarden", icon: FileSignature },
  { k: "overdracht", label: "Overdracht", icon: Calendar },
  { k: "controle", label: "Controle & verzenden", icon: CheckCircle2 },
];

export default function BidModal({ property, profileId, onClose, onSubmitted }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const askingPrice = property?.price || 0;
  const defaultValid = new Date(Date.now() + 5 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const [f, setF] = useState({
    name: "", email: "", phone: "",
    amount: "",
    valid_until: defaultValid,
    financing: "hypotheek_nodig",
    financing_reservation: true,
    financing_reservation_days: 42,
    deposit_percent: 10,
    contingencies: [],
    contingency_deadline_days: 14,
    transfer_date: "",
    notary_choice: "nader_te_bepalen",
    message_to_seller: "",
  });

  const setFin = (v) => {
    const preset = FINANCING.find((x) => x.v === v);
    setF((s) => ({ ...s, financing: v, financing_reservation: preset ? preset.res : s.financing_reservation }));
  };
  const toggleCont = (v) => setF((s) => ({ ...s, contingencies: s.contingencies.includes(v) ? s.contingencies.filter((x) => x !== v) : [...s.contingencies, v] }));

  const canProceed = () => {
    if (step === 0) return f.name && f.email && f.amount && parseFloat(f.amount) > 0;
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        property_id: property.id, name: f.name, email: f.email, phone: f.phone,
        amount: parseFloat(f.amount), financing: f.financing,
        financing_reservation: f.financing_reservation,
        financing_reservation_days: parseInt(f.financing_reservation_days) || 42,
        deposit_percent: parseFloat(f.deposit_percent) || 10,
        contingencies: f.contingencies,
        contingency_deadline_days: parseInt(f.contingency_deadline_days) || 14,
        transfer_date: f.transfer_date || null,
        notary_choice: f.notary_choice,
        valid_until: f.valid_until || null,
        message_to_seller: f.message_to_seller || null,
        profile_id: profileId || null,
      };
      const { data } = await http.post("/offers", payload);
      toast.success(`Bod verstuurd — referentie ${data.reference}`);
      onSubmitted && onSubmitted(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const amountNum = parseFloat(f.amount || 0);
  const diff = amountNum - askingPrice;
  const diffPct = askingPrice ? (diff / askingPrice) * 100 : 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose} data-testid="bid-modal">
      <div className="max-w-2xl w-full max-h-[92vh] overflow-y-auto bg-white rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase font-bold tracking-widest text-[color:var(--wv-green-dark)]">Bod uitbrengen</p>
            <h3 className="font-display text-2xl font-extrabold text-[color:var(--wv-obsidian)]">{property.title}</h3>
            <p className="text-xs text-slate-500 mt-0.5 font-mono-num">Vraagprijs {eur(askingPrice)}</p>
          </div>
          <button onClick={onClose} className="p-1" data-testid="bid-close"><X className="w-5 h-5" /></button>
        </div>

        {/* Stepper */}
        <div className="px-5 pt-4 flex gap-1 flex-wrap">
          {STEPS.map((s, i) => {
            const stepClass = (() => {
              if (i === step) return "bg-[color:var(--wv-navy)] text-white";
              if (i < step) return "bg-[color:var(--wv-green)]/10 text-[color:var(--wv-green-dark)]";
              return "bg-slate-100 text-slate-500";
            })();
            return (
              <div key={s.k} className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${stepClass}`} data-testid={`bid-step-${s.k}`}>
                <s.icon className="w-3 h-3" /> {i + 1}. {s.label}
              </div>
            );
          })}
        </div>

        <div className="p-5 space-y-4">
          {step === 0 && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-semibold">Naam *</label><input className="wv-input mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="bid-name" /></div>
                <div><label className="text-sm font-semibold">Email *</label><input type="email" className="wv-input mt-1" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="bid-email" /></div>
                <div className="col-span-2"><label className="text-sm font-semibold">Telefoon (optioneel)</label><input className="wv-input mt-1" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="bid-phone" /></div>
              </div>
              <div>
                <label className="text-sm font-semibold">Uw bod (€) *</label>
                <input type="number" className="wv-input mt-1 font-mono-num text-lg" placeholder={String(askingPrice)} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="bid-amount" />
                {amountNum > 0 && (() => {
                  let diffClass = "text-slate-500";
                  let diffText = "= vraagprijs";
                  if (diff > 0) { diffClass = "text-[color:var(--wv-green-dark)]"; diffText = `+${eur(diff)} (${diffPct.toFixed(1)}%) boven vraagprijs`; }
                  else if (diff < 0) { diffClass = "text-amber-700"; diffText = `${eur(diff)} (${diffPct.toFixed(1)}%) onder vraagprijs`; }
                  return <p className={`text-xs mt-1 font-mono-num ${diffClass}`}>{diffText}</p>;
                })()}
              </div>
              <div>
                <label className="text-sm font-semibold">Bod geldig tot</label>
                <input type="date" className="wv-input mt-1" value={f.valid_until} onChange={(e) => setF({ ...f, valid_until: e.target.value })} data-testid="bid-valid-until" />
                <p className="text-xs text-slate-500 mt-1">Standaard 5 dagen. Na deze datum vervalt uw bod automatisch tenzij verlengd.</p>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3">
              <label className="text-sm font-semibold">Financiering</label>
              <div className="grid gap-2">
                {FINANCING.map((x) => (
                  <button key={x.v} onClick={() => setFin(x.v)} className={`text-left p-3 rounded-xl border-2 ${f.financing === x.v ? "border-[color:var(--wv-navy)] bg-[color:var(--wv-navy)]/5" : "border-slate-200 hover:border-slate-300"}`} data-testid={`bid-fin-${x.v}`}>
                    <p className="font-semibold text-sm">{x.label}</p>
                  </button>
                ))}
              </div>
              <label className="mt-3 flex items-start gap-2 text-sm p-3 rounded-xl bg-slate-50 border border-slate-200">
                <input type="checkbox" checked={f.financing_reservation} onChange={(e) => setF({ ...f, financing_reservation: e.target.checked })} className="mt-1" data-testid="bid-fin-reservation" />
                <span>Onder <strong>voorbehoud van financiering</strong> — gebruikelijk bij hypotheek. Als u het voorbehoud niet tijdig kunt inroepen, kunt u het bod niet meer ontbinden op deze grond.</span>
              </label>
              {f.financing_reservation && (
                <div>
                  <label className="text-sm font-semibold">Termijn financieringsvoorbehoud (dagen)</label>
                  <input type="number" min="7" max="90" className="wv-input mt-1" value={f.financing_reservation_days} onChange={(e) => setF({ ...f, financing_reservation_days: e.target.value })} data-testid="bid-fin-days" />
                  <p className="text-xs text-slate-500 mt-1">Gebruikelijk: 6 weken (42 dagen).</p>
                </div>
              )}
              <div>
                <label className="text-sm font-semibold">Waarborgsom / bankgarantie (% van koopsom)</label>
                <input type="number" min="0" max="100" step="0.5" className="wv-input mt-1" value={f.deposit_percent} onChange={(e) => setF({ ...f, deposit_percent: e.target.value })} data-testid="bid-deposit" />
                <p className="text-xs text-slate-500 mt-1">Standaard 10%. Wordt binnen enkele weken na tekening op de derdengeldrekening van de notaris gestort.</p>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <p className="text-sm font-semibold">Ontbindende voorwaarden</p>
              <p className="text-xs text-slate-500">Vink aan welke voorwaarden voor u van toepassing zijn. U kunt uw bod alleen ontbinden binnen de gestelde termijn op basis van deze voorwaarden.</p>
              {CONTINGENCY_OPTIONS.map((c) => (
                <label key={c.v} className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer ${f.contingencies.includes(c.v) ? "border-[color:var(--wv-green)] bg-[color:var(--wv-green)]/5" : "border-slate-200"}`} data-testid={`bid-cont-${c.v}`}>
                  <input type="checkbox" checked={f.contingencies.includes(c.v)} onChange={() => toggleCont(c.v)} className="mt-1" />
                  <div><p className="font-semibold text-sm">{c.label}</p><p className="text-xs text-slate-600 mt-0.5">{c.desc}</p></div>
                </label>
              ))}
              {f.contingencies.length > 0 && (
                <div>
                  <label className="text-sm font-semibold">Termijn voorwaarden (dagen)</label>
                  <input type="number" min="7" max="60" className="wv-input mt-1" value={f.contingency_deadline_days} onChange={(e) => setF({ ...f, contingency_deadline_days: e.target.value })} data-testid="bid-cont-days" />
                  <p className="text-xs text-slate-500 mt-1">Standaard 14 dagen na acceptatie van het bod.</p>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <div>
                <label className="text-sm font-semibold">Gewenste overdrachtsdatum</label>
                <input type="date" className="wv-input mt-1" value={f.transfer_date} onChange={(e) => setF({ ...f, transfer_date: e.target.value })} data-testid="bid-transfer" />
                <p className="text-xs text-slate-500 mt-1">Datum van sleutels & notariele levering. Laat leeg voor "in overleg".</p>
              </div>
              <div>
                <label className="text-sm font-semibold">Notariskeuze</label>
                <select className="wv-input mt-1" value={f.notary_choice} onChange={(e) => setF({ ...f, notary_choice: e.target.value })} data-testid="bid-notary">
                  <option value="koper">Notaris bij koper (naar keuze koper)</option>
                  <option value="verkoper">Notaris bij verkoper</option>
                  <option value="nader_te_bepalen">Nader te bepalen</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold">Bericht aan verkoper (optioneel)</label>
                <textarea className="wv-input mt-1" rows="4" placeholder="Bijv. persoonlijke toelichting of vraag naar aanvullende afspraken (roerende zaken, opleveringsniveau, etc.)" value={f.message_to_seller} onChange={(e) => setF({ ...f, message_to_seller: e.target.value })} data-testid="bid-msg" />
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3">
              <div className="p-4 rounded-xl bg-[color:var(--wv-sand)] border border-slate-200">
                <p className="text-xs uppercase font-bold tracking-widest text-slate-500">Overzicht</p>
                <div className="mt-2 grid sm:grid-cols-2 gap-2 text-sm">
                  <div><p className="text-slate-500">Bod</p><p className="font-mono-num text-lg font-extrabold">{eur(amountNum)}</p><p className="text-xs text-slate-500">{diff === 0 ? "= vraagprijs" : diff > 0 ? `+${diffPct.toFixed(1)}% boven` : `${diffPct.toFixed(1)}% onder`} vraagprijs</p></div>
                  <div><p className="text-slate-500">Geldig tot</p><p className="font-semibold">{f.valid_until || "n.t.b."}</p></div>
                  <div><p className="text-slate-500">Financiering</p><p className="font-semibold">{FINANCING.find((x) => x.v === f.financing)?.label}</p><p className="text-xs text-slate-500">{f.financing_reservation ? `voorbehoud ${f.financing_reservation_days} dagen` : "zonder voorbehoud"}</p></div>
                  <div><p className="text-slate-500">Waarborg</p><p className="font-semibold font-mono-num">{f.deposit_percent}%</p></div>
                  <div className="sm:col-span-2"><p className="text-slate-500">Ontbindende voorwaarden</p>
                    {f.contingencies.length ? <ul className="mt-1 list-disc ml-5">{f.contingencies.map((c) => <li key={c}>{CONTINGENCY_OPTIONS.find((x) => x.v === c)?.label || c}</li>)}</ul> : <p className="text-slate-600 italic">Geen</p>}
                    {f.contingencies.length > 0 && <p className="text-xs text-slate-500 mt-1">Termijn: {f.contingency_deadline_days} dagen</p>}
                  </div>
                  <div><p className="text-slate-500">Overdracht</p><p className="font-semibold">{f.transfer_date || "in overleg"}</p></div>
                  <div><p className="text-slate-500">Notaris</p><p className="font-semibold">{f.notary_choice.replace(/_/g, " ")}</p></div>
                </div>
                {f.message_to_seller && (
                  <div className="mt-3 p-3 bg-white rounded-lg border border-slate-200">
                    <p className="text-xs uppercase font-bold text-slate-500">Bericht aan verkoper</p>
                    <p className="text-sm mt-1 whitespace-pre-wrap">{f.message_to_seller}</p>
                  </div>
                )}
              </div>
              <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2 text-sm">
                <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                <p className="text-slate-800">Uw bod is <strong>bindend</strong> zodra de verkoper deze accepteert (binnen de geldigheidstermijn). Woon Verder registreert het bod en communiceert met de verkoper.</p>
              </div>
            </div>
          )}
        </div>

        <div className="p-5 border-t border-slate-100 flex items-center justify-between gap-3">
          <button onClick={() => setStep((s) => Math.max(s - 1, 0))} disabled={step === 0} className="wv-btn-outline inline-flex items-center gap-2 disabled:opacity-40" data-testid="bid-prev">
            <ArrowLeft className="w-4 h-4" /> Vorige
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={() => canProceed() && setStep((s) => s + 1)} disabled={!canProceed()} className="wv-btn-primary inline-flex items-center gap-2 disabled:opacity-40" data-testid="bid-next">
              Volgende <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={submit} disabled={busy} className="wv-btn-green inline-flex items-center gap-2" data-testid="bid-submit">
              <Send className="w-4 h-4" /> {busy ? "Verzenden…" : "Bod definitief versturen"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
